<template lang='slm'>
  .audio-player-container :class='{ hidden }'
    audio.d-none ref='audio' @loadeddata='audioLoaded' @pause='audioPaused' @play='audioPlayed' @timeupdate='audioTimeUpdated' @ended='audioEnded' preload='none'
      source :src='"/api/file/" + fileId' type='audio/mp3'
    .audio-player
      play-pause-circle :scale='0.5' @click='playPauseClick' @touchend='playPauseClick' :playing='playing' :loading='loading' :draw-bg='false'
      .caption-container ref='captionContainer'
        .caption ref='caption'
          strong.mr-2 {{ title }}
          span {{ author }}
      .progress-container
        .my-progress :style='{ width: currentDurationPersent + "%" }'
      .duration @click='toggleRemainingDuration' {{ (showRemainingDuration ? currentDuration - duration : currentDuration) | duration }}
      .hide @click='hide'
        icon name='times'
</template>

<style lang='stylus' scoped>
  height = 48px

  .audio-player-container
    position fixed
    bottom 0
    opacity 1
    z-index 8
    width 100%
    text-align center
    height height
    transition bottom 0.3s linear, opacity 0.3s linear
    &.hidden
      bottom (-1 * height)
      opacity 0
    .audio-player
      display inline-flex
      align-items center
      padding-left 4px
      padding-right 12px
      width 100%
      max-width 1000px
      height 100%

      background-color white
      box-shadow: 0 0 5px 0 gray
      .caption-container
        max-width 30%
        @media (max-width: 575.98px)
          max-width 40%
        overflow hidden
        white-space nowrap
        text-align left
        .caption
          display inline-block
          padding 0 8px
          @media (max-width: 575.98px)
            padding 0
          transition transform 1.5s linear
      .progress-container
        margin 0 16px
        flex-grow 1
        height 2px
        background-color black
        display flex
        align-items center
        .my-progress
          border-radius 0
          height 7px
          background-color black
      .hide
        width 24px
        text-align right
</style>

<script>
import PlayPauseCircle from '@/components/PlayPauseCircle';

// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/audio
// https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Media_events

export default {
  name: 'AudioPlayer',
  components: { PlayPauseCircle },
  data() {
    return {
      title: '',
      author: '',
      fileId: 0,
      startFromPersent: 0,
      hidden: true,
      playing: false,
      loading: false,
      animatedCaptionTimeout: null,
      duration: 0,
      currentDuration: 0,
      showRemainingDuration: false,
      eventsHandler: null,
    };
  },
  methods: {
    async playAudio(options) {
      if (!(options.title && options.author && options.fileId)) return;
      if (this.fileId !== options.fileId) {
        await this.stopPlayingCurrentAudio();
      }
      this.title = options.title;
      this.author = options.author;
      // BUG startFromPersent isnt working
      this.startFromPersent = options.startFromPersent;
      this.duration = options.duration || 0;
      this.$nextTick(this.animateCaption);
      this.eventsHandler = options.eventsHandler;
      if (this.fileId === options.fileId) {
        this.startPlaying();
        return;
      }
      this.currentDuration = 0;
      this.fileId = options.fileId;
      this.$nextTick(() => {
        this.$refs.audio.load();
      });
      this.hidden = false;
      this.loading = true;
    },
    audioLoaded() {
      this.loading = false;
      // console.log('set duration from audio loaded', this.$refs.audio.duration);
      if (this.$refs.audio.duration && this.$refs.audio.duration !== Infinity) {
        // console.log('. set');
        this.duration = Math.ceil(this.$refs.audio.duration);
      }
      this.startPlaying();
    },
    audioPaused() {
      this.playing = false;
    },
    audioPlayed() {
      this.playing = true;
    },
    audioTimeUpdated() {
      this.currentDuration = Math.ceil(this.$refs.audio.currentTime);
    },
    audioEnded() {
      if (this.eventsHandler && this.eventsHandler.audioEnded) {
        this.eventsHandler.audioEnded();
      } else {
        this.hide();
      }
    },
    async hide() {
      await this.stopPlayingCurrentAudio();
      this.hidden = true;
      this.$refs.caption.style.transform = 'translateX(0px)';
      clearTimeout(this.animatedCaptionTimeout);
    },
    async stopPlayingCurrentAudio() {
      return new Promise((resolve) => {
        this.$refs.audio.pause();
        this.playing = false;
        this.loading = false;
        this.$refs.audio.currentTime = 0;
        this.currentDuration = 0;
        this.$nextTick(() => {
          this.fileId = '';
          this.eventsHandler = null;
          resolve();
        });
      });
    },
    startPlaying() {
      this.$refs.audio.play();
      if (this.startFromPersent) {
        this.$refs.audio.currentTime = this.duration * this.startFromPersent / 100;
      }
    },
    pause() {
      this.$refs.audio.pause();
    },
    animateCaption() {
      if (this.$refs.captionContainer.scrollWidth <= this.$refs.captionContainer.offsetWidth) return;
      if (this.animatedCaptionTimeout) {
        clearTimeout(this.animatedCaptionTimeout);
        this.animatedCaptionTimeout = null;
      }

      this.$refs.caption.style.transform = 'translateX(0px)';
      this.animatedCaptionTimeout = setTimeout(this.moveCaptionRight, 2000);
    },
    moveCaptionLeft() {
      this.$refs.caption.style.transform = 'translateX(0px)';
      this.animatedCaptionTimeout = setTimeout(this.moveCaptionRight, 4000);
    },
    moveCaptionRight() {
      this.$refs.caption.style.transform = `translateX(${this.$refs.captionContainer.offsetWidth - this.$refs.captionContainer.scrollWidth}px)`;
      this.animatedCaptionTimeout = setTimeout(this.moveCaptionLeft, 4000);
    },
    toggleRemainingDuration() {
      this.showRemainingDuration = !this.showRemainingDuration;
    },
    playPauseClick() {
      if (this.playing) {
        this.$refs.audio.pause();
      } else {
        this.$refs.audio.play();
      }
    },
  },
  computed: {
    currentDurationPersent() {
      return 100 * this.currentDuration / this.duration;
    },
  },
  watch: {
    playing(value) {
      if (this.eventsHandler && this.eventsHandler.setPlaying) {
        this.eventsHandler.setPlaying(value);
      }
    },
    loading(value) {
      if (this.eventsHandler && this.eventsHandler.setLoading) {
        this.eventsHandler.setLoading(value);
      }
    },
    currentDuration(value) {
      if (this.eventsHandler && this.eventsHandler.setProgressPersent) {
        this.eventsHandler.setProgressPersent(100 * value / this.duration);
      }
    },
  },
};
</script>
